/**
 * The hub stream (contracts §10). Events are hints: each names the queries to
 * refetch. There's no replay, so every open (the first one too: events can pass
 * between a page's first fetch and the socket opening) refetches all active
 * queries, a running reply's so far among them (slice 1q §5). The socket also
 * tells the hub which chat the tab shows (./watch).
 */
import { useQueryClient, type QueryClient, type QueryKey } from "@tanstack/react-query";
import { useEffect } from "react";
import { API_URL } from "./api";
import { activityKey } from "../features/activity/api";
import { agentsKey } from "../features/agent/api";
import { computerStateKey, setViewer } from "../features/computer/api";
import { threadKey, threadsKey } from "../features/chats/api";
import { liveKey, pushBlock, pushDraft, pushLive } from "../features/chats/live";
import { memoryKey } from "../features/memory/api";
import { messagingKey } from "../features/messaging/api";
import { peopleKey } from "../features/people/api";
import { integrationsKey, toolsKey } from "../features/settings/api";
import { todayKey } from "../features/today/api";
import type { Block, DraftPiece, LiveState, TurnLive } from "../features/chats/projection";
import { meKey } from "./me";
import { setHidden, setWatchSender } from "./watch";

type HubEvent = { kind: string; id: string; [hint: string]: unknown };

const text = (v: unknown) => (typeof v === "string" ? v : "");
const texts = (v: unknown) => (Array.isArray(v) ? v.filter((x) => typeof x === "string") : []);

/**
 * Every doc and the Docs list (slice 2d spec §11.6): a doc page shows its
 * linked tasks' stages and its chats' activity, so their events refresh it.
 */
const DOCS: QueryKey[] = [["doc"], ["docs"]];
/** Today (slice 4b spec §4): it shows chats, tasks, docs, approvals and routine runs. */
const TODAY: QueryKey[] = [todayKey];
const int = (v: unknown) => (typeof v === "number" && Number.isInteger(v) ? v : -1);

/** C§10's invalidation table. Kinds from later slices add their rows here. */
const INVALIDATES: Record<string, (event: HubEvent) => QueryKey[]> = {
  // Its topic icon was picked, or it was renamed (spike S-324): its tile and header.
  // Every Area page too: it lists its chats (slice 6d spec §8.2).
  thread: (e) => [threadKey(text(e.thread_id)), threadsKey, ["area"], ...TODAY],
  "thread.message": (e) => [threadKey(text(e.thread_id)), threadsKey, ["area"], ...DOCS, ...TODAY],
  // Every agent page too: its week counts the turn once it's done (slice 1e spec §2.4).
  // Activity too: a turn's row and receipt follow it (slice 2c spec §7.3).
  // The Computer page too: a turn claimed or ended is Crew working or not (slice 6c §5).
  // Every Area page too: its week counts done turns, a silent one included (slice 6d §8.2).
  turn: (e) => [
    threadKey(text(e.thread_id)),
    threadsKey,
    meKey,
    agentsKey,
    activityKey,
    computerStateKey,
    ["area"],
    ...DOCS,
    ...TODAY,
  ],
  // Every thread too: a reconnect makes an `ai_auth` turn retryable (slice 1b spec §5).
  // The Computer page too: it woke, slept, or a hold began or ended (slice 6c §5).
  computer: () => [meKey, ["thread"], computerStateKey],
  // The list and every open file (slice 1d spec §7); the editor decides what a draft does with it.
  // Every thread too: an agent's report can land after its turn's event, and
  // the reply's "Updated its notes" line comes from the turn's `updated_files`.
  // An Area's brief (slice 6d spec §5.4) carries `area_id`: its editor and the Area page's card.
  memory: (e) => [
    memoryKey,
    ["thread"],
    ...(text(e.area_id) ? [["area-brief", text(e.area_id)], ["area"]] : []),
  ],
  // A row no turn event covers: an unexpected run (slice 2c spec §5).
  activity: () => [activityKey],
  // Slice 6i: an app changed; the list reloads, and its open page tells its iframe (app-page.tsx).
  app: (e) => [["apps"], ["app", text(e.id)], ["app", text(e.id), "live"]],
  // An app account or MCP server changed (slice 3b spec §5): Integrations and
  // Browse apps, and Settings → Tools, whose "Your apps" row counts them.
  // People too: each person's row names their apps (slice 6a §6.1; Devin on #318).
  // Every Area page too: its For this area lists the accounts (slice 6d §9).
  // What asks first's list too (slice 3 §3.1): an app or a server's tools changed (Devin on #497).
  integrations: () => [integrationsKey, toolsKey, peopleKey, ["area"], ["settings", "ask-first"]],
  // A person's link confirming, linked or gone (slice 2e spec §6): Settings →
  // Messaging and its link window, and the iMessage thread made at linking,
  // its row in Chats and its header ("Not linked").
  // A phone linked or unlinked: Routines' "no phone linked" and Text it to me (slice 5 spec §8).
  messaging: () => [messagingKey, threadsKey, ["thread"], ["routines"], ["routine"]],
  // Who's in the crew (slice 6a §11): People, and the workspace line's count.
  // A join can bring a removed person back under a new name: the chats too,
  // whose rows and messages carry their name.
  people: (e) => [
    peopleKey,
    meKey,
    ...(e.status === "joined" ? [threadsKey, ["thread"], ["area"]] : []),
  ],
  // A task or its timeline (slice 2b spec §8.6): Task detail, the list, its
  // thread's cards and header, the sidebar's counts, and a receipt's touched.
  task: (e) => [
    ["task", text(e.task_id)],
    ["tasks"],
    ["area"],
    threadKey(text(e.thread_id)),
    meKey,
    activityKey,
    ...DOCS,
    ...TODAY,
  ],
  // A doc was written, archived or linked (slice 2d spec §11.6): its page, the
  // list, and every task and thread it's linked to (or was, before an unlink).
  // Every Area's brief too: its mentions show each doc's title and state (slice 6d §5.5).
  doc: (e) => [
    ["doc", text(e.doc_id)],
    ["docs"],
    ["area"],
    ["area-brief"],
    ...texts(e.task_ids).map((id) => ["task", id]),
    ...texts(e.thread_ids).map((id) => threadKey(id)),
    activityKey,
    ...TODAY,
  ],
  // A handoff was asked for, taken or ended (slice 6c §3.4): its card, its
  // thread's row ("Waiting on you") and the sidebar's agent line.
  handoff: (e) => [threadKey(text(e.thread_id)), threadsKey, meKey, computerStateKey, ...TODAY],
  // An approval was held, moved or closed (slice 3 §6): its card in the chat,
  // the sidebar's count, and its task's Waiting on.
  approval: (e) => [
    threadKey(text(e.thread_id)),
    threadsKey,
    meKey,
    ["approvals"],
    ...(text(e.task_id) ? [["task", text(e.task_id)], ["tasks"]] : []),
    ...TODAY,
  ],
  // Someone opened the computer's screen (slice 6c §6): the Computer page reads who has it.
  desktop: () => [computerStateKey],
  // An Area was made, changed, archived or restored (slice 6d spec §11): the
  // sidebar, its page and the lists of Areas.
  area: () => [meKey, ["area"], ["areas"]],
  // A routine was made, changed or deleted (slice 5 spec §10): its page, the
  // list, the sidebar's count, and Chats (a deleted routine's thread joins it).
  // The open chat too (S-525): its Made rows show routines' schedules, and the
  // event doesn't say which chats name it; only an open chat refetches.
  routine: (e) => [
    ["routine", text(e.routine_id)],
    ["routines"],
    ["area"],
    meKey,
    threadsKey,
    ["thread"],
    ...TODAY,
  ],
  // A run started, was skipped or finished (slice 5 spec §10): its next run moved.
  routine_run: (e) => [["routine", text(e.routine_id)], ["routines"], ["thread"], ...TODAY],
};

export function keysFor(event: HubEvent): QueryKey[] {
  return INVALIDATES[event.kind]?.(event) ?? [];
}

/** A `thread.token`'s block, or null when it's malformed. */
export function blockOf(event: HubEvent): Block | null {
  const block = {
    turn_id: text(event.turn_id),
    part: int(event.part),
    seq: int(event.seq),
    text: text(event.text),
  };
  return block.turn_id && block.part >= 0 && block.seq >= 1 ? block : null;
}

/** A `thread.draft`'s piece (slice 1q §2.1), or null when it's malformed. */
function draftOf(event: HubEvent): DraftPiece | null {
  const piece = {
    turn_id: text(event.turn_id),
    gen: int(event.gen),
    at: int(event.at),
    text: text(event.text),
  };
  return piece.turn_id && piece.gen >= 1 && piece.at >= 0 ? piece : null;
}

const STATES: readonly string[] = ["thinking", "writing", "working", "retrying"];

/** A `turn.live`'s state (slice 1q §2.2), or null when it's malformed. */
function liveOf(event: HubEvent): TurnLive | null {
  const turnId = text(event.turn_id);
  const state = text(event.state);
  if (!turnId || !STATES.includes(state)) return null;
  const live: TurnLive = { turn_id: turnId, state: state as LiveState };
  if (int(event.attempt) >= 1 && int(event.of) >= 1) {
    return { ...live, attempt: int(event.attempt), of: int(event.of) };
  }
  return live;
}

/**
 * One hub event. `thread.token`, `thread.draft` and `turn.live` carry
 * content: each goes to the thread's live projection, which drops it when no
 * view shows that thread.
 */
function handle(client: QueryClient, event: HubEvent) {
  const block = event.kind === "thread.token" ? blockOf(event) : null;
  if (block) pushBlock(client, text(event.thread_id), block);
  const draft = event.kind === "thread.draft" ? draftOf(event) : null;
  if (draft) pushDraft(client, text(event.thread_id), draft);
  const live = event.kind === "turn.live" ? liveOf(event) : null;
  if (live) pushLive(client, text(event.thread_id), live);
  // Slice 6c §6: someone opened the computer's screen; an open sheet that lost it says so.
  if (event.kind === "desktop") {
    setViewer(client, {
      link_id: event.id,
      name: text(event.name),
      issued_at: text(event.issued_at),
    });
  }
  for (const queryKey of keysFor(event)) void client.invalidateQueries({ queryKey });
}

export function useHubEvents(enabled: boolean) {
  const client = useQueryClient();
  useEffect(() => {
    if (!enabled) return;
    let socket: WebSocket | null = null;
    let closed = false;
    let attempts = 0;
    let retry: ReturnType<typeof setTimeout> | undefined;
    let ping: ReturnType<typeof setInterval> | undefined;

    const connect = () => {
      const origin = API_URL || location.origin;
      socket = new WebSocket(`${origin.replace(/^http/, "ws")}/api/events`);
      socket.onopen = () => {
        void client.invalidateQueries();
        attempts = 0;
        // Slice 1q §4: the chat this tab shows, again on every open.
        const open = socket;
        setWatchSender((watch) => {
          if (open?.readyState === WebSocket.OPEN) open.send(JSON.stringify({ watch }));
        });
        ping = setInterval(
          () => socket?.readyState === WebSocket.OPEN && socket.send("ping"),
          30_000,
        );
      };
      socket.onmessage = (message) => {
        if (typeof message.data !== "string" || message.data === "pong") return;
        try {
          handle(client, JSON.parse(message.data) as HubEvent);
        } catch {
          /* not an event */
        }
      };
      socket.onclose = () => {
        clearInterval(ping);
        setWatchSender(null);
        if (closed) return;
        attempts += 1;
        retry = setTimeout(connect, Math.min(30_000, 1_000 * 2 ** Math.min(attempts, 5)));
      };
    };
    // A hidden page gets no live words; shown again, it fetches the reply so far (slice 1q §6.2).
    const visibility = () => {
      setHidden(document.hidden);
      if (!document.hidden) void client.invalidateQueries({ queryKey: liveKey() });
    };
    document.addEventListener("visibilitychange", visibility);
    setHidden(document.hidden);
    connect();
    return () => {
      closed = true;
      clearTimeout(retry);
      clearInterval(ping);
      document.removeEventListener("visibilitychange", visibility);
      setWatchSender(null);
      socket?.close();
    };
  }, [client, enabled]);
}
